import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Form, FormikProvider, useFormik } from 'formik';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { useOpenTicket, useTicketSlaDeadlines } from '../core/_hooks';
import { MOCK_DEFAULT_REQUESTER } from '../core/_mock';
import {
  type Ticket,
  TicketComposerStep,
  type TicketDepartment,
  type TicketDetailsValues,
  type TicketOpenedHandler,
  type TicketPrefill,
} from '../core/_models';
import { detailsValuesForSubject, findSubjectPath, initialDetailsValues } from '../core/_utils';
import { validateTicketDetails } from '../core/_validation';
import TicketComposerFooter from './TicketComposerFooter';
import TicketDetailsStep from './TicketDetailsStep';
import TicketSubjectStep from './TicketSubjectStep';

const FORM_ID = 'ticket-composer-form';
const CONVERSATION_SUMMARY_HELP = 'Resumo da conversa feito pelo assistente. Ajuste se precisar.';

type Props = {
  catalog: TicketDepartment[];
  initialStep: TicketComposerStep;
  prefill?: TicketPrefill;
  origin: string;
  onTicketOpened: TicketOpenedHandler;
  onClose: () => void;
};

const TicketComposerForm = ({ catalog, initialStep, prefill, origin, onTicketOpened, onClose }: Props) => {
  const { currentUser } = useAuth();
  const openTicketMutation = useOpenTicket();
  const slaDeadlinesQuery = useTicketSlaDeadlines();
  const [step, setStep] = useState(initialStep);
  const [subjectId, setSubjectId] = useState(prefill?.subjectId);

  const subject = subjectId ? findSubjectPath(catalog, subjectId) : undefined;
  const form = subject?.form ?? [];
  const slaDeadlines = slaDeadlinesQuery.data ?? [];
  const subjectDeadline = slaDeadlines.find((deadline) => deadline.sla === subject?.sla);
  const requester = currentUser?.name ?? MOCK_DEFAULT_REQUESTER;
  const initialValues = initialDetailsValues(form, prefill?.fields, prefill?.description);

  const sendTicket = (values: TicketDetailsValues) => {
    if (!subjectId || openTicketMutation.isPending) return;
    const input = {
      ...values,
      subjectId,
      origin,
      requester,
      relatedTicketNumber: prefill?.relatedTicketNumber,
      conversation: prefill?.conversation,
    };
    const handleSuccess = (ticket: Ticket) => {
      onClose();
      onTicketOpened(ticket);
    };
    openTicketMutation.mutate(input, { onSuccess: handleSuccess });
  };

  const validate = (values: TicketDetailsValues) =>
    validateTicketDetails(values.store, values.description, values.fields, form);
  const formik = useFormik({ initialValues, validate, onSubmit: sendTicket });
  const { values, setValues } = formik;

  const selectSubject = (selectedSubjectId: string) => {
    const selected = findSubjectPath(catalog, selectedSubjectId);
    if (selected && selectedSubjectId !== subjectId) {
      const subjectValues = detailsValuesForSubject(values, selected.form);
      void setValues(subjectValues);
    }
    setSubjectId(selectedSubjectId);
    setStep(TicketComposerStep.DETAILS);
  };

  const goBack = () => setStep(TicketComposerStep.SUBJECT);
  const isSubjectStep = step === TicketComposerStep.SUBJECT;
  const descriptionHelp = prefill?.conversation === undefined ? undefined : CONVERSATION_SUMMARY_HELP;
  const destination = subject ? `${subject.department} › ${subject.category} › ${subject.subject}` : '';

  return (
    <FormikProvider value={formik}>
      <Modal.Body>
        {isSubjectStep ? <TicketSubjectStep onSelect={selectSubject} /> : null}
        {subject && !isSubjectStep ? (
          <Form id={FORM_ID}>
            <TicketDetailsStep
              conversationLabel={prefill?.conversationLabel}
              descriptionHelp={descriptionHelp}
              destination={destination}
              dueAt={subjectDeadline?.dueAt}
              form={form}
              instruction={subject.instruction}
              relatedTicketNumber={prefill?.relatedTicketNumber}
              sla={subject.sla}
            />
          </Form>
        ) : null}
      </Modal.Body>
      <TicketComposerFooter
        formId={FORM_ID}
        isSending={openTicketMutation.isPending}
        step={step}
        onBack={goBack}
        onCancel={onClose}
      />
    </FormikProvider>
  );
};

export default TicketComposerForm;
